import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "services/api";
import Button from "components/common/Button";
import Breadcrumbs from "components/common/Breadcrumbs";
import ShareButtons from "components/common/ShareButtons";
import BannerWithSocialIcons from "components/common/BannerWithSocialIcons";
import MapCta from "components/common/MapCta";
import LiensPratiques from "components/common/LiensPratiques";
import sharedStyles from "styles/modules/ContentPage.module.css";
import styles from "./MerchantPage.module.css";
import archiveStyle from "styles/modules/Archives.module.css";
import { ShareSocialOutline } from "react-ionicons";
import { useSubSite } from "contexts/SubSiteContext";
import useDesignSettingsData from "hooks/useDesignSettingsData";
import useBreakpoint from "hooks/useBreakpoint";
import { Helmet } from "react-helmet-async";
import Spinner from "components/common/Spinner";

const MerchantPage = () => {
  const { merchantSlug } = useParams();
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const { isSubSite, subSiteSlug } = useSubSite();
  const { designSettings, settingsLoading, error } = useDesignSettingsData();

  const isMobile = useBreakpoint();

  const merchantsURL = isSubSite
    ? `/${subSiteSlug}/commercants`
    : `/commercants`;

  useEffect(() => {
    const fetchMerchant = async () => {
      try {
        const response = await api.get(`/merchants/${merchantSlug}`);
        setMerchant(response.data || null);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération du commerçant:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMerchant();
  }, [merchantSlug]);

  if (loading)
    return (
      <div>
        <Spinner />
      </div>
    );
  if (!merchant) return <div>Aucun commerçant trouvé.</div>;

  const {
    name,
    description,
    thumbnail,
    location,
    owner,
    contactEmail,
    contactPhone,
    types,
    slug,
  } = merchant;

  if (settingsLoading)
    return (
      <div>
        <Spinner />
      </div>
    );
  if (error) return <div>Erreur : {error}</div>;

  const cleanDescription = description
    ? description.replace(/<[^>]+>/g, "")
    : "";

  const thumbnailUrl = thumbnail
    ? `${process.env.REACT_APP_MEDIA_BASE_URL}${thumbnail}`
    : null;

  return (
    <>
      <Helmet>
        <title>Arras | {name}</title>
        <meta
          name="description"
          content={
            cleanDescription.slice(0, 160)
              || `Découvrez ${name}, commerçant du marché de Noël d'Arras`
          }
        />
        <meta property="og:title" content={`Arras | ${name}`} />
        <meta
          property="og:description"
          content={
            cleanDescription.slice(0, 160)
              || `Découvrez ${name} sur le marché de Noël d'Arras`
          }
        />
        <meta
          property="og:image"
          content={thumbnailUrl || `${designSettings.defaultAssoBanner}`}
        />
        <meta
          property="og:image:alt"
          content={`Image de ${name}`}
        />
        <link
          rel="canonical"
          href={`https://www.arras.fr${merchantsURL}/${slug}`}
        />
      </Helmet>
      <div
        className={`${sharedStyles.pageWrapper} ${sharedStyles.merchantPage}`}
      >
        {/* Bannière */}
        {designSettings && designSettings.defaultAssoBanner && (
          <BannerWithSocialIcons
            imageUrl={`${designSettings.defaultAssoBanner}`}
          />
        )}

        <div className={`page-content ${sharedStyles.pageContent}`}>
          <section className={`global-section ${sharedStyles.mainSection}`}>
            <div className={`global-container ${sharedStyles.headerContainer}`}>
              <div className={sharedStyles.header}>
                <div className={archiveStyle.headingBlock}>
                  <h1>{name}</h1>
                  <Breadcrumbs pageType={"isMerchant"} element={merchant} />
                </div>

                <div className={sharedStyles.metaContainer}>
                  <div className={sharedStyles.meta}>
                    <ShareSocialOutline
                      color="currentColor"
                      height=""
                      width=""
                      className={sharedStyles.metaIcon}
                    />
                    <p className={sharedStyles.metaInfo}>Partager :</p>
                    <ShareButtons />
                  </div>
                </div>
              </div>

              {/* Tags des types */}
              {types && types.length > 0 && (
                <ul className={styles.tagList}>
                  {types.map(type => (
                    <li key={type.id} className={styles.tag}>
                      {type.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={`global-container ${sharedStyles.main}`}>
              {/* Colonne principale */}
              <div className={sharedStyles.mainColumn}>
                {/* Informations du commerce */}
                <div className={styles.contactInfo}>
                  {location && (
                    <p className={styles.infoItem}>
                      <strong>📍 Emplacement :</strong> {location}
                    </p>
                  )}

                  {owner && (
                    <p className={styles.infoItem}>
                      <strong>Propriétaire :</strong> {owner}
                    </p>
                  )}

                  {contactEmail && (
                    <p className={styles.infoItem}>
                      <strong>Email :</strong>{" "}
                      <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
                    </p>
                  )}

                  {contactPhone && (
                    <p className={styles.infoItem}>
                      <strong>Téléphone :</strong>{" "}
                      <a href={`tel:${contactPhone}`}>{contactPhone}</a>
                    </p>
                  )}
                </div>

                {/* Contenu/description du commerce */}
                {description && (
                  <div
                    className={sharedStyles.content}
                    dangerouslySetInnerHTML={{ __html: description }}
                  />
                )}

                <div className={sharedStyles.actions}>
                  <Link to={merchantsURL}>
                    <Button variant="secondary">Retour aux commerçants</Button>
                  </Link>
                  <Button variant="primary" onClick={() => window.print()}>
                    Imprimer
                  </Button>
                </div>
              </div>

              {/* Colonne latérale droite */}
              <div className={sharedStyles.sidebar}>
                {thumbnailUrl && (
                  <div className={sharedStyles.galleryThumbnail}>
                    <img
                      src={thumbnailUrl}
                      alt={name}
                      className={sharedStyles.galleryImage}
                    />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* CTA Map */}
          <MapCta />

          {/* Liens Pratiques */}
          <LiensPratiques />
        </div>
      </div>
    </>
  );
};

export default MerchantPage;

